import useMyRouting from '@context/useMyRouting/useMyRouting';
import { CircularProgress } from '@material-ui/core';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { SelectFieldProps } from '@pages/AdditionalServices/components/FieldDisplay/AdditionServiceField/SelectField';
import TextDisplayField from '@pages/AdditionalServices/components/FieldDisplay/AdditionServiceField/TextDisplayField';
import { SelectOption } from '@pages/AdditionalServices/containers/services/optionsGenerator/types';
import clsx from 'clsx';
import React, { FC, useEffect, useMemo } from 'react';

import useEcoFeeSearchContext from './useEcoFeeSearchContext';

const getSortedOptions = (groupType: 'packages' | 'products', options: SelectOption[]) => {
  switch (groupType) {
    case 'packages':
      return options
        .slice()
        .sort((a, b) => +a.origin.group.number_group - +b.origin.group.number_group);
    case 'products':
      return options;
  }
};

const EcoFeeSearchField: FC<SelectFieldProps & { groupType: 'packages' | 'products' }> = (
  props
) => {
  const {
    value,
    settings,
    tooltip,
    placeholder,
    options = [],
    isOptionsLoading,
    isFieldsDisabled,
    isExportMode,
    onChange,
    error = '',
    groupType,
  } = props;

  const [s] = useMyRouting().useSettings().useState();
  const { init, useUserSearch } = useEcoFeeSearchContext();
  const { state, makeOptionLabel, onChangeSearch, onSelectOption } = useUserSearch(
    s.primaryLanguageId,
    value,
    options,
    groupType,
    options.find((o) => o.value === value)
  );

  useEffect(() => {
    return init(s.primaryLanguageId);
  }, []);

  const isProducts = groupType === 'products';
  const isLoading = isOptionsLoading || state.LastSearchID.length > 0;
  const fieldSize = settings.FieldSize === 'small' ? 'small' : 'medium';
  const loaderSize = settings.FieldSize === 'small' ? 16 : 24;

  const sortedOptions = useMemo(
    () => getSortedOptions(groupType, state.SearchString.length === 0 ? options : state.Options),
    [state.SearchString, groupType, options, state.Options]
  );

  if (settings.IsNeedDisplayOnlyTextInfo) {
    return <TextDisplayField tooltip={tooltip} value={String(state.SelectedOption?.label ?? '')} />;
  }

  return (
    <Tooltip
      placement="top"
      title={
        tooltip.length > 0 && (
          <div dangerouslySetInnerHTML={{ __html: tooltip }} className="a-html-content" />
        )
      }
    >
      <Autocomplete
        fullWidth
        size={fieldSize}
        loading={isLoading}
        value={state.SelectedOption || null}
        disabled={isFieldsDisabled || settings.IsNeedDisableField}
        freeSolo={true}
        autoSelect={false}
        autoComplete={false}
        autoHighlight={false}
        className={clsx('a-service-field-loc-search', fieldSize, {
          products: isProducts,
        })}
        onChange={(_, __, reason) => {
          if (reason === 'clear') {
            onSelectOption(groupType, null);
            onChange(0);
          }
        }}
        filterOptions={(o) => o}
        renderInput={(params) => (
          <>
            {isExportMode && <span className="a-service-field-label">{placeholder}</span>}
            <TextField
              {...params}
              size={fieldSize}
              fullWidth
              multiline={isProducts}
              minRows={1}
              maxRows={10}
              label={!isExportMode ? placeholder : undefined}
              error={error.length > 0}
              helperText={error}
              inputProps={{
                ...params.inputProps,
                style: isProducts
                  ? {
                      whiteSpace: 'pre-line',
                      overflow: 'auto',
                      lineHeight: 1.3,
                      padding: '9px 4px 9px 5px',
                    }
                  : params.inputProps.style,
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    {isLoading && (
                      <div className={clsx('a-service-field-loc-search-loader', fieldSize)}>
                        <CircularProgress color="inherit" size={loaderSize} />
                      </div>
                    )}
                  </>
                ),
                sx: isProducts
                  ? {
                      '& .MuiInputBase-input': {
                        whiteSpace: 'pre-line',
                        overflow: 'auto',
                        height: 'auto',
                        minHeight: '1.5em',
                        fontSize: '14px',
                        color: '#000',
                      },
                    }
                  : {},
              }}
            />
          </>
        )}
        renderOption={(props, option) => {
          return (
            <li
              {...props}
              key={option.value}
              onClick={(e) => {
                onSelectOption(groupType, s.primaryLanguageId, option);
                onChange(option.value);
                props.onClick(e);
              }}
              className={clsx('a-service-field-loc-search-option', {
                checked: state.SelectedOption?.value === option.value,
              })}
            >
              <div className="option-label">{option.label}</div>
              <div className="option-description">{option.description}</div>
            </li>
          );
        }}
        onInputChange={(_, v) => onChangeSearch(groupType, v, s.primaryLanguageId)}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        getOptionLabel={(o) =>
          typeof o === 'object' ? makeOptionLabel(groupType, s.primaryLanguageId, o) : o
        }
        options={sortedOptions}
      />
    </Tooltip>
  );
};

export default EcoFeeSearchField;
